import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UsersService } from '../../../../core/services/user/users.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RolesService } from '../../../../core/services/user/roles.service';
import { SpecialismsService } from '../../../../core/services/user/specialisms.service';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { FullscreenOverlayComponent } from '../../../components/fullscreen-overlay/fullscreen-overlay.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { UserRole } from '../../../../../enums/roles.enum';
import { DateFormatterUtil } from '../../../../utils/date-format-utils';
import { DateFormatDirective } from '../../../directives/date-format.directive';
import { ToastService } from '../../../../core/services/toast.service';
import { OrganizationBranchListModel } from '../../../../core/models/user/organization-branch-list.model';
import { RoleListModel } from '../../../../core/models/user/role-list.model';
import { SpecialismListModel } from '../../../../core/models/user/specialism-list.model';
import { OrganizationListModel } from '../../../../core/models/user/organization-list.model';
import { LanguageService } from '../../../../core/services/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-create-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    FullscreenOverlayComponent,
    TranslateModule,
    BsDatepickerModule,
    DateFormatDirective,
  ],
  templateUrl: './create-edit-user.component.html',
  styleUrl: './create-edit-user.component.scss',
  providers: [provideAnimations()],
})
export class CreateEditUserComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  roles: RoleListModel[] = [];
  userId: string = '';
  specialismList: SpecialismListModel[] = [];
  organizationList: OrganizationListModel[] = [];
  organizationBranchList: OrganizationBranchListModel[] = [];
  selectedBranch: OrganizationBranchListModel | null = null;
  maxDate = new Date();
  currentUser: UserSessionModel | null = null;
  isOverlayLoading: boolean = false;
  phoneNumberPattern = '^[0-9]*$';
  userRole = UserRole;

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private usersService: UsersService,
    private rolesService: RolesService,
    private specialismsService: SpecialismsService,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private languageService: LanguageService,
    private toastService: ToastService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);

    this.userForm = this.fb.group({
      specialisms: [''],
      roleId: [null, Validators.required],
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?!.*[ğĞçÇşŞıİ])([a-zA-ZÀ-ÿĲĳ\s]*)$/u),
        ],
      ],
      surname: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?!.*[ğĞçÇşŞıİ])([a-zA-ZÀ-ÿĲĳ\s]*)$/u),
        ],
      ],
      pid: ['', [Validators.required, Validators.minLength(6)]],
      dateOfBirth: ['', Validators.required],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^\\+?[0-9]{7,15}$')],
      ],
      email: ['', [Validators.required, Validators.email]],
      branchCode: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    // this.subscriptions.push(
    //   this.languageService.language$.subscribe((lang) => {
    //     if (lang) {
    //       this.translate.use(lang);
    //     }
    //   })
    // );

    this.getUserById();
    this.currentUser = this.currentUserService.GetSession();
    this.getSpecialisms();
    this.getRoles();
    this.getOrganizations();

    if (!this.userId) {
      this.getBranchesByOrganizationCode(this.currentUser.customerCode);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getUserById() {
    this.userId = this.route.snapshot.paramMap.get('id')!;
    if (this.userId) {
      this.usersService.getUser(this.userId).subscribe({
        next: (res) => {
          this.userForm.patchValue({
            name: res.name,
            surname: res.surname,
            customerCode: res.customerCode,
            email: res.email,
            phoneNumber: res.phoneNumber,
            pid: res.pid,
            roleId: res.roleId,
            dateOfBirth: DateFormatterUtil.formatDate(res.dateOfBirth),
            specialisms: res.userSpecialsms.map(
              (item: { specialismId: string; userId: string | null }) =>
                item.specialismId
            ),
            branchCode: res.branchCode,
          });
          this.getBranchesByOrganizationCode(res.customerCode);
        },
      });
    }
  }

  getSpecialisms() {
    this.specialismsService.getSpecialisms().subscribe({
      next: (res) => {
        this.specialismList = res;
      },
    });
  }

  getRoles() {
    const allowedRoles = [
      this.userRole.GeneralPractitioner,
      this.userRole.Dermatologist,
      this.userRole.Assistant,
      this.userRole.OrganizationAdmin,
    ];

    this.rolesService.getRoles().subscribe({
      next: (res) => {
        this.roles = res.items.filter((element) =>
          allowedRoles.includes(element.name)
        );

        this.roles.forEach((role) => {
          role.name = this.translate.instant(role.name);
        });
      },
    });
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }

  formatDate(dateString: string): string {
    const selectedDate = new Date(dateString);
    const utcDate = new Date(
      selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000
    );
    return utcDate.toISOString();
  }

  formatEditDate(dateString: string): string {
    const [day, month, year] = dateString.split('-').map(Number);
    const selectedDate = new Date(Date.UTC(year, month - 1, day));
    return selectedDate.toISOString();
  }

  saveUser(e: Event) {
    e.preventDefault();
    if (this.userId) {
      // Edit user
      if (this.userForm.valid) {
        this.userForm.value.dateOfBirth = this.formatEditDate(
          this.userForm.value.dateOfBirth
        );

        const formatUserSpecialisms = [
          {
            id: this.specialismList.find(
              (specialism: SpecialismListModel) =>
                specialism.name === 'Dermatology'
            ).id,
          },
        ];
        const formData = {
          ...this.userForm.value,
          id: this.userId,
          userSpecialisms: formatUserSpecialisms,
          customerCode: this.currentUser?.customerCode,
          branchName: this.currentUser?.branchName,
          branchSelectedLanguageCode:
            this.currentUser?.branchSelectedLanguageCode,
          sendReportToPatient: this.currentUser?.sendReportToPatient,
        };
        delete formData.specialisms;

        this.usersService.editUser(this.userId, formData).subscribe({
          next: () => {
            this.isOverlayLoading = true;
            this.toastService.showToast(
              this.translate.instant('successEditUserDesc'),
              'success'
            );
            setTimeout(() => {
              this.router.navigate(['/user/list']);
            }, 1000);
          },
          complete: () => {
            setTimeout(() => {
              this.isOverlayLoading = false;
              this.router.navigate(['/user/list']);
            }, 2000);
          },
        });
      } else {
        this.toastService.showToast(
          this.translate.instant('warningAllFieldsAreRequiredDesc'),
          'warning'
        );
        this.userForm.markAllAsTouched();
      }
    } else {
      // Create new user
      if (this.userForm.valid) {
        this.userForm.value.dateOfBirth = this.formatDate(
          this.userForm.value.dateOfBirth
        );

        const formatUserSpecialisms = [
          {
            id: this.specialismList.find(
              (specialism) => specialism.name === 'Dermatology'
            ).id,
          },
        ];

        const formData = {
          ...this.userForm.value,
          specialisms: formatUserSpecialisms,
          customerCode: this.currentUser?.customerCode,
          branchCode: this.selectedBranch?.id,
          branchName: this.selectedBranch?.name,
          branchSelectedLanguageCode: this.selectedBranch?.languageCode,
          sendReportToPatient: this.currentUser?.sendReportToPatient,
        };

        this.usersService.createUser(formData).subscribe({
          next: () => {
            this.isOverlayLoading = true;
            this.toastService.showToast(
              this.translate.instant('successCreateUserDesc'),
              'success'
            );
            setTimeout(() => {
              this.router.navigate(['/user/list']);
            }, 1000);
          },

          complete: () => {
            setTimeout(() => {
              this.isOverlayLoading = false;
              this.router.navigate(['/user/list']);
            }, 2000);
          },
        });
      } else {
        this.toastService.showToast(
          this.translate.instant('warningAllFieldsAreRequiredDesc'),
          'warning'
        );
        this.userForm.markAllAsTouched();
      }
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.userForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onChangeSelectedBranch(event: any) {
    const selectedBranchId = (event.target as HTMLSelectElement).value;
    this.selectedBranch = this.organizationBranchList.find(
      (item: OrganizationBranchListModel) => item.id == selectedBranchId
    );
  }
  getBranchesByOrganizationCode(customerCode: string) {
    this.customersService
      .getCustomerBranchesByCustomerCode(customerCode)
      .subscribe({
        next: (res) => {
          this.organizationBranchList = res;
        },
      });
  }
  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
